import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { FaUserPlus, FaEye, FaTrash } from "react-icons/fa";
import { getToken } from "../../api/auth";
import {
  getAllUsers,
  getControllerByWorkerId,
  getWorkersByController,
  addWorkerToController,
} from "../../api/user";
import "./Controllers.css";

const Controllers = () => {
  const token = getToken();
  const [controllers, setControllers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedController, setSelectedController] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [controllerForAgent, setControllerForAgent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workerName, setWorkerName] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showControllerTable, setShowControllerTable] = useState(false);
  const [showAgentTable, setShowAgentTable] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const users = await getAllUsers();
        const controllersList = users.filter(
          (user) => Number(user.roleId) === 2
        );
        const agentsList = users.filter((user) => Number(user.roleId) === 3);
        setControllers(controllersList);
        setAgents(agentsList);
      } catch (err) {
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchUsers();
    }
  }, [token]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const handleControllerClick = async (controller) => {
    setSelectedController(controller);
    setSelectedAgent(null);
    setControllerForAgent(null);
    setError(null);
    setSuccess(null);
    setShowControllerTable(true);
    try {
      setLoading(true);
      const workersData = await getWorkersByController(controller.username);
      const workersArray = Array.isArray(workersData)
        ? workersData.map((username) => ({
            id: username,
            name: username,
            email: "N/A",
          }))
        : [];
      setWorkers(workersArray);
    } catch (err) {
      setError("Failed to fetch workers for controller.");
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentClick = async (agent) => {
    setSelectedAgent(agent);
    setSelectedController(null);
    setWorkers([]);
    setError(null);
    setSuccess(null);
    setShowAgentTable(true);
    try {
      setLoading(true);
      const controllerData = await getControllerByWorkerId(agent.username);
      setControllerForAgent(controllerData || null);
    } catch (err) {
      setError("Failed to fetch controller for agent.");
      setControllerForAgent(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorker = async (agentUsername) => {
    if (!agentUsername || !selectedController) {
      setError("Please select an agent to assign.");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await addWorkerToController(agentUsername, selectedController.username);
      const assignedAgent = agents.find((a) => a.username === agentUsername);
      setSuccess(
        `Agent ${assignedAgent?.name || agentUsername} assigned to ${
          selectedController.name
        }`
      );
      setWorkerName("");
      const updatedWorkersData = await getWorkersByController(
        selectedController.username
      );
      const updatedWorkersArray = Array.isArray(updatedWorkersData)
        ? updatedWorkersData.map((username) => {
            const agent = agents.find((a) => a.username === username);
            return {
              id: username,
              name: agent?.name || username,
              email: agent?.email || "N/A",
            };
          })
        : [];
      setWorkers(updatedWorkersArray);
      setIsModalOpen(false);
    } catch (err) {
      setError("Failed to assign agent. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (controller) => {
    setSelectedController(controller);
    setSelectedAgent(null);
    setWorkerName("");
    setAgentSearch("");
    setIsModalOpen(true);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="controllers-container">
      <div className="floating-bg floating-bg-1"></div>
      <div className="floating-bg floating-bg-2"></div>
      <div className="floating-bg floating-bg-3"></div>
      <div className="page-header">
        <h2 className="controllers-title">Controllers & Agents</h2>
        <p className="page-subtitle">
          Manage assignments between controllers and warehouse agents
        </p>
      </div>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {loading && <div className="loading-message">Loading...</div>}

      <div className="tables-container">
        {/* Controllers Table */}
        <div className="table-wrapper">
          <h3 className="table-title">Controllers</h3>
          <table className="glass-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {controllers.map((controller) => (
                <tr
                  key={controller.username}
                  className={`table-row ${
                    selectedController?.username === controller.username
                      ? "selected"
                      : ""
                  }`}
                >
                  <td>{controller.name}</td>
                  <td>{controller.username}</td>
                  <td>{controller.email}</td>
                  <td>
                    <button
                      className="glass-btn btn-view"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleControllerClick(controller);
                      }}
                    >
                      <FaEye size={16} /> View
                    </button>
                  </td>
                </tr>
              ))}
              {controllers.length === 0 && !loading && (
                <tr>
                  <td colSpan="4" className="empty-state">
                    No controllers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Agents Table */}
        <div className="table-wrapper">
          <h3 className="table-title">Agents</h3>
          <table className="glass-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr
                  key={agent.username}
                  className={`table-row ${
                    selectedAgent?.username === agent.username ? "selected" : ""
                  }`}
                >
                  <td>{agent.name}</td>
                  <td>{agent.username}</td>
                  <td>{agent.email}</td>
                  <td>
                    <button
                      className="glass-btn btn-view"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAgentClick(agent);
                      }}
                    >
                      <FaEye size={16} /> View
                    </button>
                  </td>
                </tr>
              ))}
              {agents.length === 0 && !loading && (
                <tr>
                  <td colSpan="4" className="empty-state">
                    No agents found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assignment Table */}
      {(showControllerTable && selectedController) ||
      (showAgentTable && selectedAgent) ? (
        <div className="table-wrapper assignment-table">
          <h3 className="table-title">
            {selectedController
              ? `Workers Assigned to ${selectedController.name}`
              : `Controller for ${selectedAgent?.name}`}
          </h3>
          {selectedController && (
            <button
              className="glass-btn btn-secondary assign-worker-btn"
              onClick={() => openModal(selectedController)}
            >
              <FaUserPlus size={16} /> Assign Worker
            </button>
          )}
          <table className="glass-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                {selectedController && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {selectedController && workers.length > 0 ? (
                workers.map((worker) => (
                  <tr key={worker.id} className="table-row">
                    <td>{worker.name}</td>
                    <td>{worker.id}</td>
                    <td>{worker.email}</td>
                    <td>
                      <button className="glass-btn btn-delete" disabled>
                        <FaTrash size={16} /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : selectedAgent && controllerForAgent ? (
                <tr className="table-row">
                  <td>{controllerForAgent.name}</td>
                  <td>{controllerForAgent.username}</td>
                  <td>{controllerForAgent.email || "N/A"}</td>
                </tr>
              ) : (
                <tr>
                  <td
                    colSpan={selectedController ? "4" : "3"}
                    className="empty-state"
                  >
                    {loading
                      ? "Loading..."
                      : selectedController
                      ? "No workers assigned"
                      : "No data available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Assign Agent to {selectedController?.name}</h3>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="agent-search-container">
              <input
                type="text"
                className="agent-search-input"
                placeholder="Search by name or username..."
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
              />
            </div>

            <div className="agents-selection-list">
              {agents && agents.length > 0 ? (
                agents
                  .filter(
                    (agent) =>
                      (agent.name
                        .toLowerCase()
                        .includes(agentSearch.toLowerCase()) ||
                        agent.username
                          .toLowerCase()
                          .includes(agentSearch.toLowerCase())) &&
                      !workers.some((worker) => worker.id === agent.username)
                  )
                  .map((agent) => (
                    <div key={agent.username} className="agent-selection-item">
                      <div className="agent-selection-info">
                        <p className="agent-name">{agent.name}</p>
                        <p className="agent-username">@{agent.username}</p>
                        <p className="agent-email">{agent.email}</p>
                      </div>
                      <button
                        className="glass-btn btn-primary assign-btn"
                        onClick={() => handleAssignWorker(agent.username)}
                        disabled={loading}
                      >
                        {loading ? "Assigning..." : "Assign"}
                      </button>
                    </div>
                  ))
              ) : (
                <p className="empty-state">
                  {agentSearch
                    ? "No agents found matching your search"
                    : "No agents available"}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="glass-btn cancel-btn"
                onClick={() => setIsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Controllers;
